import { createBrowserRouter, type RouteObject } from 'react-router';

import { AppShell } from '@/components/app-shell/app-shell';
import { NotFoundPage, PageSkeleton, RouteErrorPage } from '@/components/error-pages';
import { type FeatureModule } from '@/components/routing';
import { accessFeature } from '@/features/access/routes';
import { featureModules } from '@/features';

/** The route tree: the sign-in pages outside the app shell, the app shell with every feature's routes, the 404 page and
 * the error boundary. */
export function createAppRoutes(features: readonly FeatureModule[] = featureModules): RouteObject[] {
  const navItems = features.flatMap((feature) => feature.navItems ?? []);
  const routes = features.flatMap((feature) => feature.routes);

  return [
    // Outside the shell: there is no session here, and the shell asks /me (which would send the browser to sign in
    // again, in a loop). With their own error boundary and loading fallback, as the shell has.
    ...accessFeature.routes.map((route) => ({
      ...route,
      errorElement: <RouteErrorPage />,
      hydrateFallbackElement: <PageSkeleton />,
    })),
    {
      path: '/',
      element: <AppShell navItems={navItems} />,
      errorElement: <RouteErrorPage />,
      hydrateFallbackElement: <PageSkeleton />,
      children: [
        {
          errorElement: <RouteErrorPage />,
          children: [...routes, { path: '*', element: <NotFoundPage /> }],
        },
      ],
    },
  ];
}

export function createAppRouter() {
  return createBrowserRouter(createAppRoutes());
}
